// PROFIT'S RULES, carried over from screens/Profit.tsx unchanged (UI rebuild batch 5): which assets have a figure, when
// the report is empty, the one wording for a line, when the profit is not a clean result and the word it wears, and the
// warnings as one line of names with each sentence behind "Details". Copied, not re-derived — the old screen keeps its
// own copy until batch 5 replaces it. Keyed on the BFF's stable codes, never on its words.
import { Fragment, type ReactNode } from 'react';
import type { PnlAmounts, PnlLine, PnlNote, PnlResponse } from '../../../lib/api';

/** Assets worth a figure: anything the report actually has a number for, in the BFF's order. */
export function usedAssets(data: PnlResponse): string[] {
  const used = new Set<string>();
  const eat = (bag: PnlAmounts) => { for (const [asset, v] of Object.entries(bag)) if (v !== 0) used.add(asset); };
  for (const s of [data.income, data.expenses, data.owner.capital_in, data.owner.drawings, data.owner.other]) {
    eat(s.totals);
    for (const l of s.lines) eat(l.amounts);
  }
  eat(data.profit);
  return data.assets.filter((a) => used.has(a));
}

export function isEmpty(data: PnlResponse): boolean {
  return data.income.lines.length === 0
    && data.expenses.lines.length === 0
    && data.owner.capital_in.lines.length === 0
    && data.owner.drawings.lines.length === 0
    && data.owner.other.lines.length === 0;
}

/** The one wording for a report line: the ledger's "Spread on trades" reads "Margin". Keyed on the line's id. */
export function lineLabel(l: PnlLine): string {
  return l.key === 'spread' ? 'Margin' : l.label;
}

/** Warnings that say part of the profit is missing. */
export const INCOMPLETE_CODES: ReadonlySet<string> = new Set(['spread_not_posting', 'spread_mismatch', 'scan_truncated', 'accounts_clipped']);

/** The word a qualified profit wears — never a clean green or red result while its own page says it is partial,
 *  unchecked or an estimate. Priority: part missing > unchecked > estimate. */
const QUALIFIERS: ReadonlyArray<readonly [string, ReadonlySet<string>]> = [
  ['incomplete',  INCOMPLETE_CODES],
  ['not checked', new Set(['trade_log_unavailable'])],
  ['estimate',    new Set(['inventory_basis_missing', 'margin_uses_assumed_cost'])],
];
export function qualifierOf(warnings: PnlNote[]): string | null {
  for (const [word, codes] of QUALIFIERS) if (warnings.some((n) => codes.has(n.code))) return word;
  return null;
}

export function firstSentence(message: string): string {
  return message.split(/(?<=\.)\s+/)[0] ?? message;
}

/** A warning is one sentence — the fact and its consequence; the mechanism is in the supporting detail. */
export function shortWarning(n: PnlNote, data: PnlResponse): string {
  const first = firstSentence(n.message);
  switch (n.code) {
    case 'spread_not_posting': {
      const t = data.meta.trades_in_period;
      const booked = t !== null && t > 0 ? `${t} ${t === 1 ? 'trade' : 'trades'} booked, ` : '';
      return `Margin reads zero — ${booked}no margin reached the ledger; profit is understated.`;
    }
    case 'inventory_basis_missing':
      return 'No opening cost on the inventory held — a sale reads as all profit; set it on Inventory.';
    case 'margin_uses_assumed_cost': {
      const which = /:\s*(.+?)\.\s*Treat/.exec(n.message)?.[1];
      return `Margin on ${which ?? 'some books'} is measured against an assumed cost — an estimate until it is confirmed.`;
    }
    case 'trade_log_unavailable':
      return 'The trade log did not answer — income here is unchecked against the deals behind it.';
    case 'accounts_clipped':
      return 'More accounts than this report reads at once — some categories may be missing.';
    case 'scan_truncated':
      return `${first} Numbers are partial — narrow the period.`;
    default:
      return first;
  }
}

/** Each warning's name, at most three words. A code with no name shows its first sentence. */
export const WARNING_NAME: Readonly<Record<string, string>> = {
  spread_not_posting:       'Margin missing',
  spread_mismatch:          'Margin gap',
  inventory_basis_missing:  'No opening cost',
  margin_uses_assumed_cost: 'Assumed cost',
  trade_log_unavailable:    'Trade log unread',
  accounts_clipped:         'Accounts clipped',
  scan_truncated:           'Partial scan',
};
/** The warnings an opening cost cures: the line links to Inventory. */
export const SET_COST_CODES: ReadonlySet<string> = new Set(['inventory_basis_missing', 'margin_uses_assumed_cost', 'spread_not_posting']);
const NAMES_SHOWN = 3;

/** The line: up to three names, then "+N". A name is a nowrap piece; an unnamed code's sentence wraps. */
export function WarningNames({ warnings }: { warnings: PnlNote[] }) {
  const shown = warnings.slice(0, NAMES_SHOWN);
  const more = warnings.length - shown.length;
  const pieces: Array<{ key: string; text: string; named: boolean }> = [
    ...shown.map((n) => ({ key: n.code, text: WARNING_NAME[n.code] ?? firstSentence(n.message), named: n.code in WARNING_NAME })),
    ...(more > 0 ? [{ key: 'more', text: `+${more}`, named: true }] : []),
  ];
  return (
    <>
      {pieces.map((p, i) => (
        <Fragment key={p.key}>
          <span className={p.named ? 'whitespace-nowrap' : ''}>{p.text}{i < pieces.length - 1 ? ' ·' : ''}</span>
          {i < pieces.length - 1 ? ' ' : null}
        </Fragment>
      ))}
    </>
  );
}

/** What is behind "Details": each warning's one sentence. */
export function warningDetails(warnings: PnlNote[], data: PnlResponse): ReactNode[] {
  return warnings.flatMap((n, i) => {
    if (n.code in WARNING_NAME) return [<p key={n.code}>{shortWarning(n, data)}</p>];
    const rest = i < NAMES_SHOWN ? n.message.slice(firstSentence(n.message).length).trim() : n.message;
    return rest ? [<p key={n.code}>{rest}</p>] : [];
  });
}
